import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import type { IslandProps } from '../registry'
import { Badge, Button, EmptyState, Skeleton } from '../../shared/ui'
import { toast } from '../../lib/toast'
import { getStages } from '../../lib/stages'
import {
  loadWorkspaceSettings,
  renameBusiness,
  saveWorkspaceStages,
  type WorkspaceSummary,
} from '../../lib/workspace'
import { WORKSPACES_CHANGED_EVENT, pickCurrent, useWorkspaces } from './useWorkspaces'
import { validateBusinessName } from './validation'
import { BUSINESS_CSS } from './businessStyles'

function canManage(ws: WorkspaceSummary): boolean {
  return ws.role === 'owner' || ws.role === 'admin'
}

export default function BusinessSettingsIsland(_props: IslandProps) {
  const { workspaces, status, error, refetch } = useWorkspaces()
  const current = pickCurrent(workspaces)

  return (
    <div className="biz-page">
      <style>{BUSINESS_CSS}</style>
      <h2 className="biz-title">Business</h2>
      {(status === 'idle' || status === 'loading') && (
        <div className="biz-skeletons" aria-busy="true" aria-label="Loading">
          <Skeleton width="40" />
          <Skeleton />
        </div>
      )}
      {status === 'error' && (
        <EmptyState
          role="alert"
          title="Could not load your business"
          message={error ?? 'Something went wrong.'}
          cta={{ label: 'Retry', onClick: refetch }}
        />
      )}
      {status === 'success' && !current && (
        <EmptyState
          title="No business found"
          message="Create a business from the Businesses page."
        />
      )}
      {status === 'success' && current && (
        <>
          <RenameSection ws={current} />
          <StagesSection ws={current} />
        </>
      )}
    </div>
  )
}

// ── rename ───────────────────────────────────────────────────────────────

function RenameSection({ ws }: { ws: WorkspaceSummary }) {
  const editable = canManage(ws)
  const [name, setName] = useState(ws.name)
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setName(ws.name)
  }, [ws.id, ws.name])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy || !editable) return
    const problem = validateBusinessName(name)
    if (problem) {
      setFormError(problem)
      return
    }
    setFormError(null)
    setBusy(true)
    try {
      await renameBusiness(ws.id, name.trim())
      toast('Business renamed', 'success')
      window.dispatchEvent(new CustomEvent(WORKSPACES_CHANGED_EVENT))
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not rename the business.'
      setFormError(msg)
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="biz-section" aria-labelledby="biz-name-heading">
      <h3 id="biz-name-heading">Name {ws.kind === 'personal' && <Badge>Personal</Badge>}</h3>
      <form className="biz-inline" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="biz-field">
          <label className="biz-label" htmlFor="biz-rename">
            Business name
          </label>
          <input
            id="biz-rename"
            className="settings-input"
            type="text"
            value={name}
            readOnly={!editable}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={formError ? true : undefined}
          />
        </div>
        {editable && (
          <Button type="submit" disabled={busy || name.trim() === ws.name}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        )}
        {formError && (
          <p className="biz-error" role="alert">
            {formError}
          </p>
        )}
      </form>
      {!editable && <p className="biz-hint">Only owners and admins can rename this business.</p>}
    </section>
  )
}

// ── pipeline stages ──────────────────────────────────────────────────────

function StagesSection({ ws }: { ws: WorkspaceSummary }) {
  const editable = canManage(ws)
  const [stages, setStages] = useState<string[] | null>(null)
  const [draft, setDraft] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Pull the business's saved stages into the shared cache before reading.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await loadWorkspaceSettings()
      } catch {
        // Fall back to whatever is cached: the user's own stages or the defaults.
      }
      if (!cancelled) setStages(getStages())
    })()
    return () => {
      cancelled = true
    }
  }, [ws.id])

  if (stages === null) {
    return (
      <section className="biz-section" aria-label="Pipeline stages">
        <h3>Pipeline stages</h3>
        <div className="biz-skeletons" aria-busy="true" aria-label="Loading stages">
          <Skeleton width="70" />
          <Skeleton />
        </div>
      </section>
    )
  }

  const list = stages

  function addStage() {
    const value = draft.trim()
    if (!value) return
    if (list.some((s) => s.toLowerCase() === value.toLowerCase())) {
      setFormError('That stage is already in the list.')
      return
    }
    setFormError(null)
    setStages([...list, value])
    setDraft('')
  }

  function removeStage(i: number) {
    setFormError(null)
    setStages(list.filter((_, idx) => idx !== i))
  }

  function move(i: number, delta: -1 | 1) {
    const j = i + delta
    if (j < 0 || j >= list.length) return
    const next = [...list]
    const a = next[i]
    const b = next[j]
    if (a === undefined || b === undefined) return
    next[i] = b
    next[j] = a
    setStages(next)
  }

  async function save() {
    if (busy || list.length === 0) return
    setBusy(true)
    setFormError(null)
    try {
      await saveWorkspaceStages(ws.id, list)
      toast('Stages saved', 'success')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save the stages.'
      setFormError(msg)
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  async function reset() {
    if (busy) return
    setBusy(true)
    setFormError(null)
    try {
      await saveWorkspaceStages(ws.id, [])
      setStages(getStages())
      toast('Using your own stages', 'success')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not reset the stages.'
      setFormError(msg)
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="biz-section" aria-labelledby="biz-stages-heading">
      <h3 id="biz-stages-heading">Pipeline stages</h3>
      <p className="biz-hint">
        Changing a stage name does not rename that stage on contacts that already have it.
      </p>
      <ol className="biz-stage-list" aria-label="Stages">
        {list.map((s, i) => (
          <li key={`${s}-${i}`} className="biz-stage">
            <span className="biz-stage-name">{s}</span>
            {editable && (
              <>
                <button
                  type="button"
                  className="biz-mini"
                  aria-label={`Move ${s} up`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  Up
                </button>
                <button
                  type="button"
                  className="biz-mini"
                  aria-label={`Move ${s} down`}
                  disabled={i === list.length - 1}
                  onClick={() => move(i, 1)}
                >
                  Down
                </button>
                <button
                  type="button"
                  className="biz-mini"
                  aria-label={`Remove ${s}`}
                  onClick={() => removeStage(i)}
                >
                  Remove
                </button>
              </>
            )}
          </li>
        ))}
      </ol>
      {editable ? (
        <>
          <div className="biz-inline">
            <div className="biz-field">
              <label className="biz-label" htmlFor="biz-stage-new">
                New stage
              </label>
              <input
                id="biz-stage-new"
                className="settings-input"
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addStage()
                  }
                }}
              />
            </div>
            <Button variant="secondary" onClick={addStage}>
              Add stage
            </Button>
          </div>
          {formError && (
            <p className="biz-error" role="alert">
              {formError}
            </p>
          )}
          {list.length === 0 && <p className="biz-hint">Add at least one stage to save.</p>}
          <div className="biz-inline">
            <Button onClick={() => void save()} disabled={busy || list.length === 0}>
              Save stages
            </Button>
            <Button variant="secondary" onClick={() => void reset()} disabled={busy}>
              Use my own stages
            </Button>
          </div>
        </>
      ) : (
        <p className="biz-hint">Only owners and admins can change the stages for this business.</p>
      )}
    </section>
  )
}
